import cx from 'classnames';
import type { ComponentPropsWithoutRef } from 'react';

export type AnchorProps = {
  component: 'a';
} & ComponentPropsWithoutRef<'a'>;

export type ButtonProps = {
  component?: 'button';
} & ComponentPropsWithoutRef<'button'>;

export type CustomButtonProps = {
  size?: 'xl' | 'lg' | 'sm' | 'md' | 'xs' | 'compact';
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'link';
} & (AnchorProps | ButtonProps);

export default function Button({
  size,
  className,
  variant,
  ...restProps
}: CustomButtonProps) {
  const cn = cx('btn', className, {
    'btn-compact': size === 'compact',
    'btn-xs': size === 'xs',
    'btn-sm': size === 'sm',
    'btn-md': size === 'md',
    'btn-lg': size === 'lg',
    'btn-xl': size === 'xl',
    'btn-primary': variant === 'primary',
    'btn-secondary': variant === 'secondary',
    'btn-success': variant === 'success',
    'btn-danger': variant === 'danger',
    'btn-warning': variant === 'warning',
    'btn-info': variant === 'info',
    'btn-light': variant === 'light',
    'btn-dark': variant === 'dark',
    'btn-link': variant === 'link',
  });
  const props = { ...restProps };
  if (props.component === undefined || props.component === null) {
    props.component = 'button';
  }
  if (isAnchorProps(props)) {
    const { children, component: Component, ...p } = props;
    return (
      <Component {...p} className={cn}>
        {children}
      </Component>
    );
  } else if (isButtonProps(props) && props.component) {
    const { children, component: C, ...p } = props;
    return (
      <C {...p} className={cn}>
        {children}
      </C>
    );
  }

  return null;
}

export function isAnchorProps(
  props: AnchorProps | ButtonProps,
): props is AnchorProps {
  return props.component === 'a';
}

export function isButtonProps(
  props: AnchorProps | ButtonProps,
): props is ButtonProps {
  return props.component === 'button';
}
